import React from 'react';

/**
 * Compact "avg (covered/eligible)" affordance for the random-entry timing-edge
 * averages (random-entry baselines, fail-visible coverage).
 *
 * eligible = covered + coveredEmpty + missing — every result with an outcome
 * that could have matched a baseline bucket. covered results contribute to the
 * average; coveredEmpty buckets were computed but had no qualifying market;
 * missing buckets have no baseline evidence (pending or unsupported). Partial coverage (covered < eligible)
 * is flagged amber because the average is estimated from a subset.
 */
export const computeEdgeEligibleCount = (counts: {
    coveredCount?: number | undefined;
    coveredEmptyCount?: number | undefined;
    missingCount?: number | undefined;
}): number => (counts.coveredCount ?? 0) + (counts.coveredEmptyCount ?? 0) + (counts.missingCount ?? 0);

export const EdgeCoverageCell: React.FC<{
    value: number | null | undefined;
    coveredCount?: number | undefined;
    coveredEmptyCount?: number | undefined;
    missingCount?: number | undefined;
}> = ({ value, coveredCount, coveredEmptyCount, missingCount }) => {
    const covered = coveredCount ?? 0;
    const eligible = computeEdgeEligibleCount({ coveredCount, coveredEmptyCount, missingCount });

    const valueNode =
        value === null || value === undefined ? (
            <span className="text-zinc-500">-</span>
        ) : (
            <span className={value >= 0 ? 'text-green-400' : 'text-red-400'}>{value.toFixed(2)}%</span>
        );

    if (eligible === 0) return valueNode;

    const isPartial = covered < eligible;

    const coverageTitle = isPartial
        ? `Average covers ${covered} of ${eligible} eligible results — ${missingCount ?? 0} without baseline evidence (pending or unsupported), ` +
          `${coveredEmptyCount ?? 0} computed-empty (no qualifying market existed). Partial coverage: interpret with reduced trust.`
        : `Average covers all ${eligible} eligible results.`;

    return (
        <span title={coverageTitle}>
            {valueNode}
            <span className={`text-xs ml-1 ${isPartial ? 'text-amber-400' : 'text-zinc-500'}`}>
                ({covered}/{eligible})
            </span>
        </span>
    );
};
